import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Table cell that sets the native title attribute only when text is truncated.
 * Measures an inner block span — td scrollWidth/clientWidth false-positives on
 * padded ellipsis cells (short values like "Georgie" / "30" were getting title).
 */
const OverflowTitleTd = ({ titleText, className, children, ...rest }) => {
  const measureRef = useRef(null);
  const [title, setTitle] = useState(undefined);

  const updateTitle = useCallback(() => {
    const el = measureRef.current;
    if (!el) {
      return;
    }

    const text =
      titleText == null || titleText === '' ? '' : String(titleText);
    // 1px tolerance for subpixel rounding
    const isTruncated = text !== '' && el.scrollWidth - el.clientWidth > 1;

    setTitle(isTruncated ? text : undefined);
  }, [titleText]);

  useLayoutEffect(() => {
    updateTitle();
    // Remeasure after layout/fonts settle (avoids sticky false titles)
    const raf = window.requestAnimationFrame(() => {
      updateTitle();
    });

    const el = measureRef.current;
    if (!el) {
      return () => window.cancelAnimationFrame(raf);
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateTitle)
        : null;

    if (resizeObserver) {
      resizeObserver.observe(el);
    }

    window.addEventListener('resize', updateTitle);

    return () => {
      window.cancelAnimationFrame(raf);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateTitle);
    };
  }, [updateTitle, children]);

  return (
    <td title={title} className={className} {...rest}>
      <span
        ref={measureRef}
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
    </td>
  );
};

OverflowTitleTd.propTypes = {
  titleText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  children: PropTypes.node,
};

OverflowTitleTd.defaultProps = {
  titleText: '',
  className: '',
  children: null,
};

export default OverflowTitleTd;
